import React, { useEffect, useState } from "react";
import Draggable from "react-draggable";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader, Table, Row, Col, Spinner } from "reactstrap";
import { useTranslation } from "react-i18next";
import { GetLanguageString } from "../../helper/Components";
import { GetCustomStyles, GetSelectedLanguage, getFileNameWithoutExtension } from "../../helper/Method";
import moment from "moment";
import { applyClassToCharacters } from "../../../constants/const";
import { FaUniversity, FaHashtag, FaMoneyBillWave, FaCalendarAlt, FaRegCommentDots, FaUser, FaFolder, FaMapMarkerAlt } from "react-icons/fa";
import { ApiFinance } from "../ApiFinance";
import { openAttachmentInNewTab, downloadAttachmentDirect } from "../attachmentHelper";

const IncomeDetailsModal = ({ transaction, setModal }) => {
  const { t } = useTranslation();
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [attachmentIds, setAttachmentIds] = useState([]);
  const [loadingAttachments, setLoadingAttachments] = useState(false);
  const [loadingViewId, setLoadingViewId] = useState(null);
  const [loadingDownloadId, setLoadingDownloadId] = useState(null);

  useEffect(() => {
    const transNo =
      transaction?.transactionNo ||
      transaction?.transNo ||
      transaction?.trans_no ||
      transaction?.accountTransactionId ||
      transaction?.fundId ||
      transaction?.id ||
      transaction?.Id;

    const fallbackFileId =
      transaction?.fileId ||
      transaction?.file_id ||
      transaction?.FileId ||
      transaction?.details?.find((d) => d.fileId || d.file_id || d.FileId)?.fileId ||
      transaction?.details?.find((d) => d.fileId || d.file_id || d.FileId)?.file_id ||
      transaction?.details?.find((d) => d.fileId || d.file_id || d.FileId)?.FileId;

    if (transNo) {
      setLoadingAttachments(true);
      ApiFinance()
        .GetTransactionAttachments(transNo)
        .then((res) => {
          if (Array.isArray(res.data) && res.data.length > 0) {
            setAttachmentIds(res.data);
          } else if (fallbackFileId) {
            setAttachmentIds([{ fileId: fallbackFileId, fileName: "Attachment 1" }]);
          } else {
            setAttachmentIds([]);
          }
        })
        .catch(() => {
          if (fallbackFileId) {
            setAttachmentIds([{ fileId: fallbackFileId, fileName: "Attachment 1" }]);
          } else {
            setAttachmentIds([]);
          }
        })
        .finally(() => setLoadingAttachments(false));
    } else if (fallbackFileId) {
      setAttachmentIds([{ fileId: fallbackFileId, fileName: "Attachment 1" }]);
    }
  }, [transaction]);

  const handleDownloadAttachment = (item, index) => {
    const defaultName = typeof item === "object" && item.fileName ? item.fileName : `attachment_${index + 1}`;
    if (downloadAttachmentDirect(item, defaultName)) {
      return;
    }
    const fileId = typeof item === "object" ? (item.fileId || item.id) : item;
    setLoadingDownloadId(fileId);
    ApiFinance()
      .ViewAttachmentByFileId(fileId)
      .then((res) => {
        let fileName = defaultName;
        const contentDisposition = res.headers?.["content-disposition"] || res.headers?.["Content-Disposition"];
        if (contentDisposition) {
          const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/i);
          if (match && match[1]) {
            fileName = match[1].replace(/['"]/g, "").trim();
          }
        } else if (!fileName.includes(".")) {
          const contentType = res.headers?.["content-type"] || res.headers?.["Content-Type"];
          if (contentType?.includes("jpeg") || contentType?.includes("jpg")) fileName += ".jpg";
          else if (contentType?.includes("png")) fileName += ".png";
          else if (contentType?.includes("pdf")) fileName += ".pdf";
        }

        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        setTimeout(() => window.URL.revokeObjectURL(url), 100);
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => setLoadingDownloadId(null));
  };

  const handleViewAttachment = (item) => {
    if (openAttachmentInNewTab(item)) {
      return;
    }
    const fileId = typeof item === "object" ? (item.fileId || item.id) : item;
    setLoadingViewId(fileId);
    ApiFinance()
      .ViewAttachmentByFileId(fileId)
      .then((res) => {
        const contentType = res.headers?.["content-type"] || res.headers?.["Content-Type"] || "application/pdf";
        const file = new Blob([res.data], { type: contentType });
        const fileURL = URL.createObjectURL(file);
        window.open(fileURL, "_blank");
        setTimeout(() => URL.revokeObjectURL(fileURL), 1000);
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => setLoadingViewId(null));
  };

  const handleDrag = (e, ui) => {
    setPosition((prev) => ({
      x: prev.x + ui.deltaX,
      y: prev.y + ui.deltaY,
    }));
  };

  const toggle = () => setModal(false);

  // Filter: only show details where amount is negative
  const filteredDetails = (transaction?.details || []).filter(item => (item.amount || 0) < 0);

  const isCategoryInfaq =
    transaction?.category?.label1?.toLowerCase() === "infaq" ||
    transaction?.category?.label1?.toLowerCase() === "infaq_rafeeqat" ||
    filteredDetails.some(
      (item) =>
        item.category?.label1?.toLowerCase() === "infaq" ||
        item.category?.label1?.toLowerCase() === "infaq_rafeeqat",
    );

  const isPureInfaq =
    transaction?.category?.label1?.toLowerCase() === "infaq" ||
    filteredDetails.some(
      (item) => item.category?.label1?.toLowerCase() === "infaq",
    );

  const rafiqName =
    transaction?.personId ||
    filteredDetails.find((d) => d.realName)?.realName ||
    "";

  // Check if categories are uniform across all detail rows
  const categorySet = new Set(
    filteredDetails
      .map((d) => d.category?.label || d.category?.label1)
      .filter(Boolean)
  );
  const isCategorySame = categorySet.size <= 1;
  const categoryLabel = isCategorySame
    ? (transaction?.category?.label || filteredDetails.find((d) => d.category?.label)?.category?.label || "")
    : "";

  const getAttachmentLabel = (item, index = 0) => {
    const rawFileName = typeof item === "object" ? (item.fileName || item.name || "") : "";
    const lowerName = (rawFileName || "").toLowerCase();

    // 1. Differentiate by explicit filename keywords
    if (lowerName.includes("expense") || lowerName.includes("اخراجات")) {
      return t("expense_receipts_attachment") || "Expense Receipts";
    }
    if (lowerName.includes("income") || lowerName.includes("آمدنی") || lowerName.includes("summary")) {
      return t("income_summary_attachment") || "Income Summary";
    }
    if (lowerName.includes("loan") || lowerName.includes("قرض")) {
      return t("halqa_loan_letter") || "Halqa Loan Details Letter";
    }
    if (lowerName.includes("deposit") || lowerName.includes("ڈپازٹ") || lowerName.includes("slip") || lowerName.includes("سلپ")) {
      return t("deposit_slip") || "Deposit Slip";
    }

    // 2. Differentiate by category
    const catCode = (transaction?.category?.label1 || transaction?.categoryCode || "").toLowerCase();
    const catLabel = (transaction?.category?.label || categoryLabel || "").toLowerCase();
    const isTarbiati =
      catCode.includes("tarbiati_course") ||
      catLabel.includes("tarbiati course") ||
      filteredDetails.some((d) => {
        const c = (d.category?.label1 || d.categoryCode || "").toLowerCase();
        const l = (d.category?.label || "").toLowerCase();
        return c.includes("tarbiati_course") || l.includes("tarbiati course");
      });

    if (isTarbiati) {
      if (index === 0) return t("expense_receipts_attachment") || "Expense Receipts";
      if (index === 1) return t("income_summary_attachment") || "Income Summary";
      return `${t("attachment") || "Attachment"} ${index + 1}`;
    }

    const isLoanMarkaz =
      catCode === "loan_from_markaz" ||
      catCode === "loan_markaz" ||
      catLabel.includes("loan from markaz") ||
      filteredDetails.some((d) => {
        const c = (d.category?.label1 || d.categoryCode || "").toLowerCase();
        const l = (d.category?.label || "").toLowerCase();
        return c === "loan_from_markaz" || c === "loan_markaz" || l.includes("loan from markaz");
      });
    if (isLoanMarkaz) {
      return `${t("halqa_loan_letter") || "Halqa Loan Details Letter"}${attachmentIds.length > 1 ? ` ${index + 1}` : ""}`;
    }

    if (isCategoryInfaq) {
      return `${t("deposit_slip") || "Deposit Slip"}${attachmentIds.length > 1 ? ` ${index + 1}` : ""}`;
    }

    // 3. Clean custom user-provided filename
    if (rawFileName && !lowerName.startsWith("attachment")) {
      return getFileNameWithoutExtension(rawFileName);
    }

    // 4. Default fallback: Attachment 1, Attachment 2 (like expense)
    return `${t("attachment") || "Attachment"}${attachmentIds.length > 1 ? ` ${index + 1}` : ""}`;
  };

  // Show Subhead column in table
  const showSubheadInTable = true;
  const showCategoryInTable = !isCategorySame;

  // Show memo column if any split row is NOT infaq or tarbiati_course_infaq
  const showMemo = !isCategoryInfaq && (
    filteredDetails.some(item => {
      const code = item.category?.label1?.toLowerCase() || "";
      return code !== "infaq" && code !== "tarbiati_course_infaq";
    }) || (transaction?.category?.label1?.toLowerCase() !== "infaq" &&
      transaction?.category?.label1?.toLowerCase() !== "tarbiati_course_infaq")
  );

  // Calculate total based on absolute values (negative displayed as positive)
  const totalCalculated = filteredDetails.reduce((sum, item) => sum + Math.abs(item.amount || 0), 0);

  const totalColumns = 1 + (showCategoryInTable ? 1 : 0) + 1 + (showSubheadInTable ? 1 : 0) + 1 + (showMemo ? 1 : 0);
  const isUrdu = GetSelectedLanguage() === "ur";

  const isHelpFromMuqamiTanzeem = transaction?.category?.label1?.toLowerCase() === "help_from_muqami_tanzeem" ||
    filteredDetails.some(d => d.category?.label1?.toLowerCase() === "help_from_muqami_tanzeem");
  const helpFromUnitName = isHelpFromMuqamiTanzeem
    ? (transaction?.helpFromUnitName || filteredDetails.find(d => d.helpFromUnitName)?.helpFromUnitName || "")
    : "";

  const addressToDisplay = transaction?.address || transaction?.Address || filteredDetails.find(d => d.address || d.Address)?.address || filteredDetails.find(d => d.address || d.Address)?.Address || "";

  return (
    <Draggable position={position} onDrag={handleDrag} cancel=".no-drag">
      <Modal isOpen className="inmodal" size="xl" autoFocus={false}>
        <ModalHeader className="modal-title" tag="h4">
          <GetLanguageString props="finance_other_income_entry_details" />
        </ModalHeader>

        <ModalBody className="no-drag">
          {/* Theme-Matched Dashboard Summary Block */}
          <div style={{ marginBottom: "30px" }}>
            <Row>
              <Col md="4" sm="6" className="mb-3">
                <div style={{ padding: "16px", backgroundColor: "#ffffff", borderRadius: "4px", border: "1px solid #e7eaec", borderLeft: "4px solid #1ab394", boxShadow: "0 2px 2px rgba(0,0,0,0.05)", height: "100%" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <div style={{ fontSize: "11px", textTransform: "uppercase", color: "#676a6c", fontWeight: "600", letterSpacing: "0.5px" }}>
                        <GetLanguageString props="funds_account" />
                      </div>
                      <div style={{ fontSize: "15px", fontWeight: "600", marginTop: "8px", color: "#2f4050" }}>
                        {transaction?.bankName || "-"}
                      </div>
                    </div>
                    <FaUniversity style={{ color: "#1ab394", fontSize: "20px", opacity: 0.8 }} />
                  </div>
                </div>
              </Col>

              <Col md="4" sm="6" className="mb-3">
                <div style={{ padding: "16px", backgroundColor: "#ffffff", borderRadius: "4px", border: "1px solid #e7eaec", borderLeft: "4px solid #1ab394", boxShadow: "0 2px 2px rgba(0,0,0,0.05)", height: "100%" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <div style={{ fontSize: "11px", textTransform: "uppercase", color: "#676a6c", fontWeight: "600", letterSpacing: "0.5px" }}>
                        <GetLanguageString props="funds_reference" />
                      </div>
                      <div style={{ fontSize: "15px", fontWeight: "600", marginTop: "8px", color: "#2f4050" }}>
                        {transaction?.reference?.trim().split(/\s+/).pop() || "-"}
                      </div>
                    </div>
                    <FaHashtag style={{ color: "#1ab394", fontSize: "20px", opacity: 0.8 }} />
                  </div>
                </div>
              </Col>

              <Col md="4" sm="6" className="mb-3">
                <div style={{ padding: "16px", backgroundColor: "#1ab394", borderRadius: "4px", border: "1px solid #1ab394", boxShadow: "0 2px 2px rgba(0,0,0,0.05)", height: "100%" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <div style={{ fontSize: "11px", textTransform: "uppercase", color: "#ffffff", fontWeight: "600", letterSpacing: "0.5px", opacity: 0.9 }}>
                        <GetLanguageString props="finance_other_income_total_amount" />
                      </div>
                      <div style={{ fontSize: "20px", fontWeight: "700", marginTop: "4px", color: "#ffffff" }}>
                        {new Intl.NumberFormat().format(transaction?.amount || 0)}
                      </div>
                    </div>
                    <FaMoneyBillWave style={{ color: "#ffffff", fontSize: "24px", opacity: 0.9 }} />
                  </div>
                </div>
              </Col>

              <Col md="4" sm="6" className="mb-3">
                <div style={{ padding: "16px", backgroundColor: "#ffffff", borderRadius: "4px", border: "1px solid #e7eaec", borderLeft: "4px solid #1ab394", boxShadow: "0 2px 2px rgba(0,0,0,0.05)", height: "100%" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <div style={{ fontSize: "11px", textTransform: "uppercase", color: "#676a6c", fontWeight: "600", letterSpacing: "0.5px" }}>
                        <GetLanguageString props="funds_date" />
                      </div>
                      <div style={{ fontSize: "15px", fontWeight: "600", marginTop: "8px", color: "#2f4050" }}>
                        {transaction?.date ? moment(transaction.date, "YYYY-MM-DD").format("DD/MM/YYYY") : "-"}
                      </div>
                    </div>
                    <FaCalendarAlt style={{ color: "#1ab394", fontSize: "20px", opacity: 0.8 }} />
                  </div>
                </div>
              </Col>

              {categoryLabel && (
                <Col md="4" sm="6" className="mb-3">
                  <div style={{ padding: "16px", backgroundColor: "#ffffff", borderRadius: "4px", border: "1px solid #e7eaec", borderLeft: "4px solid #1ab394", boxShadow: "0 2px 2px rgba(0,0,0,0.05)", height: "100%" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div>
                        <div style={{ fontSize: "11px", textTransform: "uppercase", color: "#676a6c", fontWeight: "600", letterSpacing: "0.5px" }}>
                          <GetLanguageString props="funds_source" />
                        </div>
                        <div style={{ fontSize: "15px", fontWeight: "600", marginTop: "8px", color: "#2f4050" }}>
                          {applyClassToCharacters(categoryLabel, "ur")}
                        </div>
                      </div>
                      <FaFolder style={{ color: "#1ab394", fontSize: "20px", opacity: 0.8 }} />
                    </div>
                  </div>
                </Col>
              )}

              {addressToDisplay && (
                <Col md="4" sm="6" className="mb-3">
                  <div style={{ padding: "16px", backgroundColor: "#ffffff", borderRadius: "4px", border: "1px solid #e7eaec", borderLeft: "4px solid #1ab394", boxShadow: "0 2px 2px rgba(0,0,0,0.05)", height: "100%" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div>
                        <div style={{ fontSize: "11px", textTransform: "uppercase", color: "#676a6c", fontWeight: "600", letterSpacing: "0.5px" }}>
                          <GetLanguageString props="member_address" />
                        </div>
                        <div style={{ fontSize: "14px", fontWeight: "600", marginTop: "8px", color: "#2f4050" }}>
                          {applyClassToCharacters(addressToDisplay, "ur")}
                        </div>
                      </div>
                      <FaMapMarkerAlt style={{ color: "#1ab394", fontSize: "20px", opacity: 0.8 }} />
                    </div>
                  </div>
                </Col>
              )}

              {isPureInfaq && rafiqName && (
                <Col md="4" sm="6" className="mb-3">
                  <div style={{ padding: "16px", backgroundColor: "#ffffff", borderRadius: "4px", border: "1px solid #e7eaec", borderLeft: "4px solid #1ab394", boxShadow: "0 2px 2px rgba(0,0,0,0.05)", height: "100%" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div>
                        <div style={{ fontSize: "11px", textTransform: "uppercase", color: "#676a6c", fontWeight: "600", letterSpacing: "0.5px" }}>
                          <GetLanguageString props="member_report_rafiq_serial_number" />
                        </div>
                        <div style={{ fontSize: "15px", fontWeight: "600", marginTop: "8px", color: "#2f4050" }}>
                          {applyClassToCharacters(rafiqName || "-", "ur")}
                        </div>
                      </div>
                      <FaUser style={{ color: "#1ab394", fontSize: "20px", opacity: 0.8 }} />
                    </div>
                  </div>
                </Col>
              )}

              {helpFromUnitName && (
                <Col md="4" sm="6" className="mb-3">
                  <div style={{ padding: "16px", backgroundColor: "#ffffff", borderRadius: "4px", border: "1px solid #e7eaec", borderLeft: "4px solid #1ab394", boxShadow: "0 2px 2px rgba(0,0,0,0.05)", height: "100%" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div>
                        <div style={{ fontSize: "11px", textTransform: "uppercase", color: "#676a6c", fontWeight: "600", letterSpacing: "0.5px" }}>
                          <GetLanguageString props="help_from_unit_name" />
                        </div>
                        <div style={{ fontSize: "15px", fontWeight: "600", marginTop: "8px", color: "#2f4050" }}>
                          {applyClassToCharacters(helpFromUnitName, "ur")}
                        </div>
                      </div>
                      <FaUniversity style={{ color: "#1ab394", fontSize: "20px", opacity: 0.8 }} />
                    </div>
                  </div>
                </Col>
              )}

              {transaction?.memo && (
                <Col md="4" sm="6" className="mb-3">
                  <div style={{ padding: "16px", backgroundColor: "#ffffff", borderRadius: "4px", border: "1px solid #e7eaec", borderLeft: "4px solid #1ab394", boxShadow: "0 2px 2px rgba(0,0,0,0.05)", height: "100%" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div>
                        <div style={{ fontSize: "11px", textTransform: "uppercase", color: "#676a6c", fontWeight: "600", letterSpacing: "0.5px" }}>
                          <GetLanguageString props="transactions_memo" />
                        </div>
                        <div style={{ fontSize: "14px", marginTop: "8px", color: "#2f4050", fontStyle: "italic" }}>
                          {transaction?.memo}
                        </div>
                      </div>
                      <FaRegCommentDots style={{ color: "#1ab394", fontSize: "20px", opacity: 0.8 }} />
                    </div>
                  </div>
                </Col>
              )}
            </Row>
          </div>

          {/* Styled Items Grid Table */}
          <div style={{ borderRadius: "4px", overflow: "hidden", border: "1px solid #e7eaec", boxShadow: "0 2px 2px rgba(0,0,0,0.05)" }}>
            <Table hover responsive style={{ margin: 0, backgroundColor: "#ffffff" }}>
              <thead style={{ borderBottom: "2px solid #1ab394" }}>
                <tr>
                  <th className="text-center" style={{ color: "#676a6c", fontSize: "12px", textTransform: "uppercase", padding: "12px", fontWeight: "600", letterSpacing: "0.5px" }}>#</th>
                  {showCategoryInTable && (
                    <th style={{ color: "#676a6c", fontSize: "12px", textTransform: "uppercase", padding: "12px", fontWeight: "600", letterSpacing: "0.5px" }}><GetLanguageString props="funds_source" /></th>
                  )}
                  <th style={{ color: "#676a6c", fontSize: "12px", textTransform: "uppercase", padding: "12px", fontWeight: "600", letterSpacing: "0.5px" }}><GetLanguageString props="funds_income_head" /></th>
                  {showSubheadInTable && (
                    <th style={{ color: "#676a6c", fontSize: "12px", textTransform: "uppercase", padding: "12px", fontWeight: "600", letterSpacing: "0.5px" }}><GetLanguageString props="funds_income_sub_head" /></th>
                  )}
                  {showMemo && (
                    <th style={{ color: "#676a6c", fontSize: "12px", textTransform: "uppercase", padding: "12px", fontWeight: "600", letterSpacing: "0.5px" }}><GetLanguageString props="transactions_memo" /></th>
                  )}
                  <th className="text-center" style={{ color: "#676a6c", fontSize: "12px", textTransform: "uppercase", padding: "12px", fontWeight: "600", letterSpacing: "0.5px" }}><GetLanguageString props="funds_add_fund_amount" /></th>
                </tr>
              </thead>
              <tbody>
                {filteredDetails.length === 0 ? (
                  <tr>
                    <td colSpan={totalColumns} className="text-center" style={{ padding: "30px", color: "#676a6c", fontStyle: "italic" }}>
                      <GetLanguageString props="common_no_data_available" />
                    </td>
                  </tr>
                ) : (
                  filteredDetails.map((item, index) => (
                    <tr key={index} style={{ borderBottom: "1px solid #e7eaec" }}>
                      <td className="text-center" style={{ padding: "15px", color: "#676a6c" }}>{index + 1}</td>
                      {showCategoryInTable && (
                        <td style={{ padding: "15px", color: "#676a6c" }}>
                          {applyClassToCharacters(item.category?.label || "-", "ur")}
                        </td>
                      )}
                      <td className={isUrdu ? "ur" : ""} style={{ padding: "15px", color: "#676a6c" }}>{item.accountName || item.account || "-"}</td>
                      {showSubheadInTable && (
                        <td className={isUrdu ? "ur" : ""} style={{ padding: "15px", color: "#676a6c" }}>{item.dimension2Name || item.dimension2Id || "-"}</td>
                      )}
                      {showMemo && (
                        <td className={isUrdu ? "ur" : ""} style={{ padding: "15px", color: "#676a6c", fontStyle: "italic" }}>
                          {((item.category?.label1?.toLowerCase() === "infaq" || item.category?.label1?.toLowerCase() === "tarbiati_course_infaq")
                            ? ""
                            : item.memo) || "-"}
                        </td>
                      )}
                      <td className="text-center" style={{ padding: "15px", color: "#1ab394", fontWeight: "700" }}>
                        {new Intl.NumberFormat().format(Math.abs(item.amount || 0))}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              {filteredDetails.length > 0 && (
                <tfoot style={{ backgroundColor: "#f3f3f4", borderTop: "2px solid #e7eaec" }}>
                  <tr>
                    <td colSpan={2 + (showCategoryInTable ? 1 : 0) + (showSubheadInTable ? 1 : 0) + (showMemo ? 1 : 0)} style={{ textAlign: isUrdu ? "left" : "right", padding: "15px", color: "#2f4050", fontWeight: "700" }}>
                      <GetLanguageString props="create_report_conducted_total" />:
                    </td>
                    <td className="text-center" style={{ padding: "15px", color: "#1ab394", fontWeight: "700" }}>
                      {new Intl.NumberFormat().format(totalCalculated)}
                    </td>
                  </tr>
                </tfoot>
              )}
            </Table>
          </div>

          {/* Attachment Section */}
          {loadingAttachments && attachmentIds.length === 0 && !transaction?.fileId && !transaction?.file_id ? (
            <div className="text-center py-3">
              <Spinner size="sm" color="primary" /> <small className="text-muted ms-2"><GetLanguageString props="loading" />...</small>
            </div>
          ) : (attachmentIds.length > 0 || transaction?.fileId || transaction?.file_id) ? (
            <div style={{ marginTop: "20px", padding: "16px 20px", backgroundColor: "#ffffff", borderRadius: "8px", border: "1px solid #e7eaec", borderLeft: "4px solid #0EA5E9", boxShadow: "0 2px 4px rgba(0,0,0,0.04)" }}>
              <div style={{ fontSize: "14px", fontWeight: "600", color: "#0284C7", marginBottom: "12px", display: "flex", alignItems: "center", gap: "8px" }}>
                <i className="fa fa-paperclip" style={{ fontSize: "15px" }}></i>
                <span><GetLanguageString props="attached_files" /> ({attachmentIds.length > 0 ? attachmentIds.length : 1})</span>
              </div>
              <div className="d-flex flex-column gap-2">
                {(attachmentIds.length > 0 ? attachmentIds : [{ fileId: transaction?.fileId || transaction?.file_id, fileName: "" }]).map((item, index) => {
                  const itemFileId = typeof item === "object" ? (item.fileId || item.id) : item;
                  const displayName = getAttachmentLabel(item, index);
                  return (
                    <div key={itemFileId || index} className="d-flex justify-content-between align-items-center p-2 rounded border bg-light" style={{ borderColor: "#E5E7EB" }}>
                      <div className="d-flex align-items-center gap-2">
                        <i className="fa fa-file text-muted"></i>
                        <span style={{ fontSize: "13px", fontWeight: "500", color: "#374151" }}>
                          {displayName}
                        </span>
                      </div>
                      <div className="d-flex gap-2">
                        <Button
                          color="white"
                          size="sm"
                          style={{ padding: "6px 14px", fontSize: "12px", fontWeight: "500", borderRadius: "6px", display: "inline-flex", alignItems: "center", gap: "6px", border: "1px solid #D1D5DB" }}
                          disabled={loadingViewId === itemFileId || loadingDownloadId === itemFileId}
                          onClick={() => handleViewAttachment(item)}
                        >
                          {loadingViewId === itemFileId ? (
                            <Spinner size="sm" style={{ width: "12px", height: "12px" }} />
                          ) : (
                            <i className="fa fa-eye text-primary"></i>
                          )}
                          <GetLanguageString props="member_add_rafiq_request_view_document" />
                        </Button>
                        <Button
                          color="white"
                          size="sm"
                          style={{ padding: "6px 14px", fontSize: "12px", fontWeight: "500", borderRadius: "6px", display: "inline-flex", alignItems: "center", gap: "6px", border: "1px solid #D1D5DB" }}
                          disabled={loadingViewId === itemFileId || loadingDownloadId === itemFileId}
                          onClick={() => handleDownloadAttachment(item, index)}
                        >
                          {loadingDownloadId === itemFileId ? (
                            <Spinner size="sm" style={{ width: "12px", height: "12px" }} />
                          ) : (
                            <i className="fa fa-download text-success"></i>
                          )}
                          <GetLanguageString props="member_add_rafiq_request_download_document" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}
        </ModalBody>

        <ModalFooter>
          <Button
            color="white"
            className={GetCustomStyles().btn_style}
            onClick={toggle}
          >
            <GetLanguageString props="common_close" />
          </Button>
        </ModalFooter>
      </Modal>
    </Draggable>
  );
};

export default IncomeDetailsModal;
